import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Bright%20airy%20modern%20loft%20interior%20with%20white%20walls%2C%20hanging%20cascading%20plants%2C%20natural%20sunlight%20streaming%20through%20large%20industrial%20windows%2C%20light%20wooden%20floors%2C%20contemporary%20minimalist%20furniture%2C%20fresh%20botanical%20lifestyle%20photography%2C%20editorial%20quality%2C%20clean%20and%20spacious%20aesthetic%2C%20warm%20inviting%20atmosphere%2C%20Scandinavian%20influence%20with%20Japanese%20wabi%20sabi%20touches%2C%20no%20people%20visible&width=1800&height=1200&seq=hero-v2-kakobuy&orientation=landscape"
          alt="Kakobuy Spreadsheet resource background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/40 via-background-50/20 to-background-50/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="flex flex-col items-center text-center">
          {/* Tag */}
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100/70 text-primary-700 text-xs font-semibold tracking-wide uppercase">
              <i className="ri-sparkling-line"></i>
              Updated Product Database
            </span>
          </div>

          {/* Headline */}
          <h1 className="animate-fade-in-up-delay-1 mt-6 font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-foreground-950 leading-[1.08] max-w-4xl">
            The Ultimate <span className="text-primary-500">Kakobuy Spreadsheet</span> Resource
          </h1>

          {/* Subheadline */}
          <p className="animate-fade-in-up-delay-2 mt-6 text-base md:text-lg text-foreground-600 max-w-2xl leading-relaxed">
            Discover thousands of organized product finds, shopping spreadsheets, category guides, and research tools designed to help you find products faster.
          </p>

          {/* Trust indicators */}
          <div className="animate-fade-in-up-delay-2 mt-5 flex flex-wrap items-center justify-center gap-5">
            {['Updated Product Database', 'Organized Categories', 'Beginner Friendly Guides', 'Global Shopping Resources'].map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
                {item}
              </span>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="animate-fade-in-up-delay-3 mt-8 flex flex-col sm:flex-row items-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
            >
              Explore Kakobuy Spreadsheet
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-primary-600 border-2 border-primary-400 hover:bg-primary-50 rounded-full transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>

          {/* Category Dropdown in Hero */}
          <div className="animate-fade-in-up-delay-3 mt-10 relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="inline-flex items-center gap-3 px-6 py-3 bg-background-50/90 backdrop-blur-sm border border-background-200/70 rounded-2xl text-foreground-800 hover:border-primary-300 transition-all cursor-pointer"
            >
              <i className="ri-grid-line text-lg text-primary-500"></i>
              <span className="font-semibold text-sm">Browse All Categories</span>
              <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[520px] bg-background-50 rounded-2xl border border-background-200/70 shadow-[0_12px_40px_rgba(0,0,0,0.08)] overflow-hidden z-20 animate-fade-in-up">
                <div className="grid grid-cols-2 gap-1 p-3 max-h-[400px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors group"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 group-hover:bg-primary-50 transition-colors shrink-0">
                        <i className={`${cat.icon} text-lg text-foreground-500 group-hover:text-primary-500 transition-colors`}></i>
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                          {cat.name}
                        </span>
                        <span className="text-xs text-foreground-400">{cat.count} finds</span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-2xl text-foreground-300"></i>
      </div>
    </section>
  );
}